import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActionSheet, Toast } from 'antd-mobile-v5'
import { useIntl } from 'react-intl'
import {
  FUIOU_H5_PAY_TYPE,
  FUIOU_WPOS_PAY_TYPE,
  isValidFuiouUrl,
  isValidWposQrContent,
  pickFuiouChannel,
} from '../components/FormTools/utils'
import { useFuiouPaymentPolling } from './useFuiouPaymentPolling'

// WPOS 渠道白名单（前端防御纵深，后端仍须独立校验）
const ALLOWED_PAY_TYPES = [FUIOU_WPOS_PAY_TYPE.WECHAT, FUIOU_WPOS_PAY_TYPE.ALIPAY]

/**
 * CLUB VIP 富友支付 hook（H5Pay / WPOS 双通道）。
 * 三个页面 (BuyCards / ClubEcardsEquity / ClubVipChangeEcards) 共享同一支付逻辑。
 *
 * @param {Object} options
 * @param {string|Function} options.type - 支付类型（如 'ECARDS_CHANGE' / 'CLUB_VIP'），或返回类型的函数
 * @param {Function} options.fuiouPay - 富友建单接口 (useClubVipFuiouPay.runAsync)
 * @param {Function} options.checkFuiouStatus - 富友支付状态查询 (useClubVipPaymentStatus.runAsync)
 * @param {Function} [options.onBeforePay] - 发起支付前的回调（如埋点）
 * @returns {Object} { toPay, nativeQrCode, nativePayDisplay, setNativePayDisplay }
 */
export function useClubVipPayment({ type, fuiouPay, checkFuiouStatus, onBeforePay }) {
  const { formatMessage } = useIntl()
  const [nativeQrCode, setNativeQrCode] = useState('')
  // 应付金额（CHNA-2618：clubVip getPaymentUrl 响应新增 price/currency，含升级差价、与实扣一致）
  const [nativePrice, setNativePrice] = useState(null)
  const [nativeCurrency, setNativeCurrency] = useState(null)
  const [nativePayDisplay, setNativePayDisplay] = useState(false)
  const mountedRef = useRef(true)
  const payingRef = useRef(false)
  const actionSheetRef = useRef(null)
  const reloadTimerRef = useRef(null)

  // 组件卸载时：标记卸载、清理命令式 ActionSheet
  useEffect(() => {
    // StrictMode 下 effect 会 setup→cleanup→setup 重跑：cleanup 置 false 后必须复位，
    // 否则重挂载后 mountedRef 恒 false，支付回调全部失效
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (actionSheetRef.current) {
        actionSheetRef.current.close()
        actionSheetRef.current = null
      }
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current)
        reloadTimerRef.current = null
      }
    }
  }, [])

  const resolveType = useMemo(() => (typeof type === 'function' ? type : () => type), [type])

  const checkStatus = useCallback(
    async (params) => {
      const res = await checkFuiouStatus(params)
      return !!(res && res.paid)
    },
    [checkFuiouStatus]
  )

  // 支付成功/超时/轮询失败回调（关闭弹窗并提示）
  const handlePaymentSuccess = useCallback(() => {
    setNativePayDisplay(false)
    Toast.show({
      icon: 'success',
      content: formatMessage({ id: 'fuiou-pay-success' }),
      duration: 2000,
      afterClose: () => {
        reloadTimerRef.current = setTimeout(() => window.location.reload(), 500)
      },
    })
  }, [formatMessage])

  // 超时是"查不到结果"而非"支付失败"：用户可能已付款，fuiou-pay-timeout 保持中性文案、
  // 不用失败图标——失败语义会诱导用户重新下单重复付款（二次扣款）
  const handlePaymentTimeout = useCallback(() => {
    setNativePayDisplay(false)
    Toast.show({ content: formatMessage({ id: 'fuiou-pay-timeout' }) })
  }, [formatMessage])

  // 连续轮询失败（网络中断）只提示"仍在确认中"，不关闭弹窗：轮询由 hook 继续重试，
  // 直到超时才由 handlePaymentTimeout 收尾（查询不到结果 ≠ 支付失败）
  const handlePollingError = useCallback(() => {
    Toast.show({ content: formatMessage({ id: 'fuiou-poll-confirming' }) })
  }, [formatMessage])

  const { startPolling } = useFuiouPaymentPolling({
    checkStatus,
    isActive: nativePayDisplay,
    onSuccess: handlePaymentSuccess,
    onTimeout: handlePaymentTimeout,
    onPollingError: handlePollingError,
  })

  const showPayFailToast = useCallback(
    () => Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-pay-fail' }) }),
    [formatMessage]
  )

  // WPOS 扫码：按所选渠道（WECHAT/ALIPAY）建单并展示二维码
  const startWposPay = useCallback(
    async (type, payType) => {
      if (payingRef.current) return
      if (!ALLOWED_PAY_TYPES.includes(payType)) {
        showPayFailToast()
        return
      }
      payingRef.current = true
      Toast.show({
        icon: 'loading',
        content: formatMessage({ id: 'fuiou-pay-loading' }),
      })
      try {
        const result = await fuiouPay({ type, channel: 'WPOS', payType })
        if (!mountedRef.current) return
        // WPOS 单码通道返回渠道二维码内容（qr.alipay.com / weixin://），只做本地编码展示，
        // 校验用二维码内容白名单（兼容历史富友聚合链接形态）；H5Pay 整页跳转仍用域名白名单
        // URL 缺失或内容非法均提示地址异常
        if (!result?.url || !isValidWposQrContent(result.url)) {
          // 订单已创建只是链接异常：指引去购买记录继续，避免用户重复下单产生孤儿单
          Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-order-url-invalid' }) })
          return
        }
        setNativeQrCode(result.url)
        setNativePrice(result.price)
        setNativeCurrency(result.currency)
        setNativePayDisplay(true)
        startPolling({ type })
      } catch (e) {
        if (mountedRef.current) showPayFailToast()
      } finally {
        payingRef.current = false
      }
    },
    [fuiouPay, formatMessage, startPolling, showPayFailToast]
  )

  // 弹出渠道选择（WPOS）：防重复弹出，组件卸载时由外层 effect 清理
  const showChannelSheet = useCallback(
    (type) => {
      if (actionSheetRef.current) return
      actionSheetRef.current = ActionSheet.show({
        actions: [
          { text: formatMessage({ id: 'fuiou-pay-channel-wechat' }), key: FUIOU_WPOS_PAY_TYPE.WECHAT },
          { text: formatMessage({ id: 'fuiou-pay-channel-alipay' }), key: FUIOU_WPOS_PAY_TYPE.ALIPAY },
        ],
        onAction: (action) => startWposPay(type, action.key),
        // 选完渠道立即关闭面板并触发 onClose 复位 ref，否则面板残留会堵死重试流
        closeOnAction: true,
        onClose: () => {
          actionSheetRef.current = null
        },
      })
    },
    [startWposPay, formatMessage]
  )

  // 发起支付（富友 H5Pay（微信内）/ WPOS（PC 扫码）双通道），防重入
  const toPay = useCallback(async () => {
    if (payingRef.current) return
    if (onBeforePay) onBeforePay()
    const type = resolveType()
    const isWechat = pickFuiouChannel() !== 'WPOS'

    if (isWechat) {
      // 微信内 H5Pay：直接拉起
      payingRef.current = true
      Toast.show({
        icon: 'loading',
        content: formatMessage({ id: 'fuiou-pay-loading' }),
      })
      try {
        const result = await fuiouPay({ type, payType: FUIOU_H5_PAY_TYPE.WECHAT_JSAPI })
        const url = result?.url
        if (!url) {
          if (mountedRef.current) showPayFailToast()
          return
        }
        if (!isValidFuiouUrl(url)) {
          if (mountedRef.current) {
            // 订单已创建只是链接异常：指引去购买记录继续，避免用户重复下单产生孤儿单
            Toast.show({ icon: 'fail', content: formatMessage({ id: 'fuiou-order-url-invalid' }) })
          }
          return
        }
        window.location.replace(url)
      } catch (e) {
        if (mountedRef.current) showPayFailToast()
      } finally {
        payingRef.current = false
      }
    } else {
      // WPOS PC 扫码：先选渠道（微信/支付宝），再拉对应单码
      showChannelSheet(type)
    }
  }, [onBeforePay, resolveType, fuiouPay, formatMessage, showChannelSheet, showPayFailToast])

  return { toPay, nativeQrCode, nativePrice, nativeCurrency, nativePayDisplay, setNativePayDisplay }
}
